import React, { useState, useEffect } from "react";
import { getOpenConferences } from "../../services/api";
import { manuscriptTypes } from "../../constants/types";

/**
 * PaperConferenceSelection Component
 * 
 * Step 1 of the paper submission wizard.
 * Handles conference and manuscript type selection.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.handleChange - Form field change handler
 * @param {Array} props.conferences - Available conferences from parent component
 */
const PaperConferenceSelection = ({
  formData,
  handleChange,
  conferences: propConferences
}) => {
  const [conferences, setConferences] = useState([]);
  const [loadingConferences, setLoadingConferences] = useState(true);
  const [conferenceError, setConferenceError] = useState("");

  /**
   * Initialize conferences from props or fetch if not provided
   */
  useEffect(() => {
    if (propConferences && propConferences.length > 0) {
      // Use conferences passed from parent component
      setConferences(propConferences);
      setLoadingConferences(false);
    } else {
      // Fetch conferences if not provided
      const fetchOpenConferences = async () => {
        try {
          setLoadingConferences(true);
          setConferenceError("");
          const openConferences = await getOpenConferences();
          setConferences(openConferences);
        } catch (error) {
          console.error("Error fetching open conferences:", error);
          setConferenceError("Failed to load available conferences. Please try again.");
        } finally {
          setLoadingConferences(false);
        }
      };

      fetchOpenConferences();
    }
  }, [propConferences]); 

  const LOCALE = 'en-GB';

  const formatDate = (dateString, includeYear) => {
    const options = includeYear 
      ? { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Etc/GMT' }
      : { month: 'long', day: 'numeric', timeZone: 'Etc/GMT' };
    return new Date(dateString).toLocaleDateString(LOCALE, options);
  };

  return (
    <div className="row">
      <div className="col-12">
        <h4 className="mb-4">
          Select the conference and manuscript type to begin your submission.
        </h4>

        {/* Conference Selection */}
        <div className="mb-4">
          <label htmlFor="conferenceId" className="form-label fw-bold">
            Conference <span className="text-danger">*</span>
          </label>
          
          {conferenceError && (
            <div className="alert alert-warning mb-2" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {conferenceError}
            </div>
          )}
          
          <select
            id="conferenceId"
            name="conferenceId"
            className="form-select"
            value={formData.conferenceId}
            onChange={handleChange}
            disabled={loadingConferences}
            required
          >
            <option value="">
              {loadingConferences ? "Loading Conferences..." : "Select a Conference:"}
            </option>
            {conferences.map((conference) => (
              <option key={conference._id} value={conference._id}>
                {conference.title}, {conference.location}, {formatDate(conference.startDate, false)} - {formatDate(conference.endDate, true)}
              </option>
            ))}
          </select>
          
          <div className="form-text">
            Choose the conference where you want to submit your paper.
          </div>
        </div>

        {/* Manuscript Type Selection */}
        <div className="mb-4">
          <label htmlFor="manuscriptType" className="form-label fw-bold">
            Manuscript Type <span className="text-danger">*</span>
          </label>
          <select
            id="manuscriptType"
            name="manuscriptType"
            className="form-select"
            value={formData.manuscriptType}
            onChange={handleChange}
            required
          >
            <option value="">Select manuscript type...</option>
            {manuscriptTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <div className="form-text">
            Select the type of manuscript you are submitting.
          </div>
        </div>

        {/* Required fields notice */}
        <div className="mb-3">
          <p className="text-muted">
            <span className="text-danger">*</span> Required
          </p>
        </div>

        {/* Information Box
        <div className="alert alert-info" role="alert">
          <h6 className="alert-heading">
            <i className="bi bi-info-circle me-2"></i>
            Next Steps
          </h6>
          <p className="mb-0">
            After selecting your conference and manuscript type, you'll upload your document. 
            Our AI system will automatically extract the title, abstract, and author information 
            from your document to pre-fill the submission form.
          </p>
        </div> */}
      </div>
    </div>
  );
};

export default PaperConferenceSelection;
